import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { TIME_ZONES } from '@drayalliance/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppUIActionContext } from '@client/components/App/contexts';
import { toggleAppModal } from '@client/state/ui/actions';
import Button from '@client/components/Button';
import DateTimePicker from '@client/components/DateTimePicker';
import { raiseToast, TOAST_POSITION } from '@client/components/Toaster';
import SystemToast from '@client/components/SystemToast/component';
import SystemTooltip from '@client/components/SystemTooltip';
import { handleCustomError } from '@client/utils/errors';
import { logAnalyticsClick } from '@client/utils/analytics';
import { GateUpdateContext } from '../../hooks';
import { findInvalidStopInstantDates } from '../../utils';
import DateConfirmationDialog from '../DateConfirmationDialog';
import {
  DriverGpsRelation,
  UpdateDriverGpsRelationIngressEgress,
  updateDriverGpsRelationIngressEgress,
  updateStopInstantActualDateTime
} from './requests';

import './style.css';

export interface IngressEgressOwner {
  legUuid: string;
  driverUuid: string | null;
}

export type GateType = 'outgate' | 'ingate';

interface IngressEgressFieldProps {
  className?: string;
  stopUuid: string;
  stopDepartmentUuid: string | null;
  stopInstant: { uuid: string; appointmentStart: string | null; appointmentEnd: string | null };
  relations?: DriverGpsRelation[];
  owner: IngressEgressOwner | null;
  timezone?: TIME_ZONES;
  onApplied: () => Promise<void>;
  /** When set, an applied outgate egress / ingate ingress is also written to the stop instant's actualDateTime. */
  gateType?: GateType | null;
  onGateTimeApplied?: (actualDateTime: string) => void;
}

function toIsoString(value: moment.MomentInput, timezone: TIME_ZONES): string | null {
  const momentValue = typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(value)
    ? moment.tz(value, 'MM/DD/YYYY HH:mm', true, timezone)
    : moment(value);

  return value && momentValue.isValid() ? momentValue.toISOString() : null;
}

function formatTime(value: string | null, timezone?: TIME_ZONES, dateFormat = 'MM/DD') {
  if (!value) {
    return '--';
  }

  const momentValue = timezone ? moment(value).tz(timezone) : moment(value);

  return momentValue.format(`${dateFormat} HH:mm${timezone ? ' z' : ''}`);
}

function IngressEgressField(props: IngressEgressFieldProps) {
  const { da5747: maxPickableDays } = useFlags();
  const appUIDispatch = useAppUIActionContext();
  const runGateUpdate = useContext(GateUpdateContext);
  const baseClassName = 'ingress-egress-field';
  const ref = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const applyPending = useRef(false);
  const [draft, setDraft] = useState<{ ingressTime: string | null; egressTime: string | null }>({
    ingressTime: null,
    egressTime: null
  });

  /**
   * The relation is keyed by (stopInstantUuid, driverUuid). Prefer the owning leg's current driver, but fall back to
   * any relation on this stop instant so a reassigned driver doesn't hide times that were already captured.
   */
  const relation = useMemo(() => {
    const stopInstantRelations = props.relations?.filter((r) => r.stopInstantUuid === props.stopInstant.uuid) ?? [];

    return (
      stopInstantRelations.find((r) => r.driverUuid === props.owner?.driverUuid) ?? stopInstantRelations[0] ?? null
    );
  }, [props.relations, props.stopInstant.uuid, props.owner?.driverUuid]);
  const timezone = useMemo(() => props.timezone ?? TIME_ZONES.AMERICA_LOS_ANGELES, [props.timezone]);

  const legUuid = relation?.legUuid ?? props.owner?.legUuid;
  const driverUuid = relation?.driverUuid ?? props.owner?.driverUuid;
  const editable = !!legUuid && !!driverUuid;

  useEffect(() => {
    if (!editing || applying || confirming) {
      return undefined;
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setEditing(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setEditing(false);
        ref.current?.querySelector<HTMLElement>('[role="button"]')?.focus();
      }
     };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    }
  }, [editing, applying, confirming]);

  const ingressChanged =
    !!draft.ingressTime &&
    (!relation?.ingressTime || !moment(draft.ingressTime).isSame(relation.ingressTime, 'minute'));
  const egressChanged =
    !!draft.egressTime &&
    (!relation?.egressTime || !moment(draft.egressTime).isSame(relation.egressTime, 'minute'));
  const effectiveIngress = draft.ingressTime ?? relation?.ingressTime;
  const effectiveEgress = draft.egressTime ?? relation?.egressTime;
  const egressBeforeIngress =
    !!effectiveIngress && !!effectiveEgress && moment(effectiveEgress).isBefore(moment(effectiveIngress));

  const handleOpen = () => {
    if (!editable || editing) {
      return;
    }

    setDraft({ ingressTime: relation?.ingressTime ?? null, egressTime: relation?.egressTime ?? null });
    setEditing(true);
  };

  const handleApply = async () => {
    if (applyPending.current) {
      return;
    }

    applyPending.current = true;
    logAnalyticsClick('apply ingress egress', 'dispatch', { legId: legUuid });

    const update: UpdateDriverGpsRelationIngressEgress = {
      stopInstantUuid: props.stopInstant.uuid,
      legUuid: legUuid!,
      driverUuid: driverUuid!,
      stopUuid: props.stopUuid,
      stopDepartmentUuid: props.stopDepartmentUuid,
      appointmentStart: toIsoString(props.stopInstant.appointmentStart, timezone),
      appointmentEnd: toIsoString(props.stopInstant.appointmentEnd, timezone)
    };

    /** Only send what changed: gps-service flags every submitted time as manual, freezing it against GPS correlation. */
    if (ingressChanged) {
      update.ingressTime = draft.ingressTime!;
    }

    if (egressChanged) {
      update.egressTime = draft.egressTime!;
    }

    let gateTime: string | null = null;

    if (props.gateType === 'outgate' && egressChanged) {
      gateTime = draft.egressTime;
    } else if (props.gateType === 'ingate' && ingressChanged) {
      gateTime = draft.ingressTime;
    }

    const apply = async () => {
      await updateDriverGpsRelationIngressEgress([update]);

      if (gateTime) {
        await updateStopInstantActualDateTime(props.stopInstant.uuid, gateTime);
        props.onGateTimeApplied?.(gateTime);
      }

      await props.onApplied();
      setEditing(false);
    };

    const persist = async () => {
      setApplying(true);

      try {
        if (runGateUpdate) {
          await runGateUpdate(apply);
        } else {
          await apply();
        }
      } catch (err) {
        raiseToast(
          <SystemToast
            type={SystemToast.Type.ERROR}
            message={handleCustomError(err, 'Unable to update ingress/egress times')}
          />,
          { position: TOAST_POSITION.BOTTOM_LEFT }
        );
      } finally {
        applyPending.current = false;
        setApplying(false);
      }
    };

    const invalidDates = gateTime
      ? findInvalidStopInstantDates(
          { stopInstant: [{ id: props.stopInstant.uuid, attributes: { actualDateTime: gateTime } }] },
          maxPickableDays
        )
      : [];

    if (invalidDates.length) {
      setConfirming(true);
      let confirmationClosed = false;
      const closeConfirmation = () => {
        confirmationClosed = true;
        setConfirming(false);
        appUIDispatch(toggleAppModal(false));
      };

      appUIDispatch(
        toggleAppModal(
          true,
          <DateConfirmationDialog
            invalidDates={invalidDates}
            onConfirm={() => {
              if (confirmationClosed) {
                return;
              }
              closeConfirmation();
              persist();
            }}
            onCancel={() => {
              if (confirmationClosed) {
                return;
              }
              applyPending.current = false;
              closeConfirmation();
            }}
          />,
          true
        )
      );
    } else {
      await persist();
    }
  };

  let className = baseClassName;

  if (props.className) {
    className += ` ${props.className}`;
  }

  if (editable && !editing) {
    className += ` ${baseClassName}--editable`;
  }

  const hasTimes = !!relation?.ingressTime || !!relation?.egressTime;
  const tooltipId = `ingress-egress-${props.stopInstant.uuid}`;

  return (
    <div
      ref={ref}
      className={className}
      title={editable ? undefined : 'Assign a driver to set ingress/egress'}
      onClick={handleOpen}
    >
      <div
        className={`${baseClassName}__text`}
        data-tooltip-id={hasTimes && !editing ? tooltipId : undefined}
        role={editable ? 'button' : undefined}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={editable ? 0 : undefined}
        aria-expanded={editable ? editing : undefined}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen();
          }
        }}
      >
        {hasTimes ? (
          `In ${formatTime(relation!.ingressTime, props.timezone)} · Out ${formatTime(relation!.egressTime, props.timezone)}`
        ) : (
          <span className={`${baseClassName}__placeholder`}>Ingress / Egress</span>
        )}
      </div>
      {hasTimes && (
        <SystemTooltip
          id={tooltipId}
          items={[
            { title: 'Ingress', message: formatTime(relation!.ingressTime, props.timezone, 'MM/DD/YYYY') },
            { title: 'Egress', message: formatTime(relation!.egressTime, props.timezone, 'MM/DD/YYYY') }
          ]}
        />
      )}
      {editing && (
        <div className={`${baseClassName}__dd`}>
          <div className={`${baseClassName}__dd-pickers`}>
            <div className={`${baseClassName}__dd-picker`}>
              <span className={`${baseClassName}__dd-label`}>Ingress</span>
              <DateTimePicker
                value={draft.ingressTime}
                onChange={(value: moment.MomentInput) =>
                  setDraft((prev) => ({ ...prev, ingressTime: toIsoString(value, timezone) }))
                }
                timezone={timezone}
              />
            </div>
            <div className={`${baseClassName}__dd-picker`}>
              <span className={`${baseClassName}__dd-label`}>Egress</span>
              <DateTimePicker
                value={draft.egressTime}
                onChange={(value: moment.MomentInput) =>
                  setDraft((prev) => ({ ...prev, egressTime: toIsoString(value, timezone) }))
                }
                timezone={timezone}
              />
            </div>
          </div>
          <div className={`${baseClassName}__dd-footer`}>
            {egressBeforeIngress && <span className={`${baseClassName}__dd-error`}>Egress is before ingress</span>}
            <Button
              theme="2"
              label="Apply"
              loading={applying}
              disabled={applying || confirming || egressBeforeIngress || (!ingressChanged && !egressChanged)}
              onClick={handleApply}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default IngressEgressField;
